import {
  Button,
  DialogActionTrigger,
  DialogTitle,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { FaPlus } from "react-icons/fa"

import { QuestionsService } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import { BulkUploadErrorModal } from "./BulkUploadErrorModal"

function BulkUploadDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadErrors, setUploadErrors] = useState<
    Array<{ row: number; error: string }>
  >([])
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const mutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      return QuestionsService.bulkUploadQuestions({
        formData: { file } as any,
      })
    },
    onSuccess: (data) => {
      if (data.error_count > 0) {
        setUploadErrors(data.errors ?? [])
        setErrorModalOpen(true)
        showSuccessToast(
          `Uploaded ${data.success_count} questions. ${data.error_count} errors.`,
        )
      } else {
        showSuccessToast(
          `Successfully uploaded ${data.success_count} questions.`,
        )
        setSelectedFile(null)
        setIsOpen(false)
      }
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["questions"] })
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUpload = () => {
    if (selectedFile) {
      mutation.mutate(selectedFile)
    }
  }

  const handleDownloadTemplate = () => {
    const csv = [
      [
        "question_text",
        "option_a",
        "option_b",
        "option_c",
        "option_d",
        "correct_answer",
        "rationale",
        "subjects",
        "topics",
        "allow_practice_quiz",
      ].join(","),
      [
        "What is 2+2?",
        "3",
        "4",
        "5",
        "6",
        "b",
        "2+2 is 4",
        '"Math,Arithmetic"',
        '"Addition,Basics"',
        "true",
      ].join(","),
    ].join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "questions-bulk-upload-template.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <DialogRoot
        size={{ base: "xs", md: "md" }}
        placement="center"
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
      >
        <DialogTrigger asChild>
          <Button variant="outline">
            <FaPlus fontSize="16px" />
            Upload CSV
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload Questions</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={4} align="stretch">
              <Text>
                Upload a CSV file with questions. Required columns:
                question_text, option_a, option_b, ... (at least two contiguous
                option columns), correct_answer (matching option letter),
                rationale, subjects (comma-separated), topics (comma-separated),
                allow_practice_quiz (true/false, defaults to false if empty).
              </Text>
              <Button
                alignSelf="flex-start"
                size="sm"
                variant="subtle"
                onClick={handleDownloadTemplate}
              >
                Download sample CSV
              </Button>
              <Field label="Choose CSV File">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  id="csv-file"
                  aria-label="Choose CSV File"
                />
              </Field>
              {selectedFile && (
                <Text fontSize="sm" color="gray">
                  Selected: {selectedFile.name}
                </Text>
              )}
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button variant="subtle" colorPalette="gray">
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              variant="solid"
              onClick={handleUpload}
              disabled={!selectedFile}
              loading={mutation.isPending}
            >
              Upload
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
      <BulkUploadErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        errors={uploadErrors}
      />
    </>
  )
}

export default BulkUploadDialog
